import { use, useEffect, useMemo } from "react";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { createContext, useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { defaultError } from "@-/com.npmjs.react-context-default-error";
import useLatest from "@-/com.npmjs.use-latest";
import type {
  VoiceStudyController,
  VoiceStudyLifecycle,
  VoiceStudySnapshot,
  VoiceStudyTurn,
} from "#voice-study-controller";
import { VoiceStudyEnabledIfProvidedContext } from "#voice-study-enabled-if-provided-context";
import type { VoiceStudySessionHandle } from "#voice-study-session-handle";

/** Provided by `VoiceStudyProvider` (the Learn stack); reading it anywhere else throws. */
export const VoiceStudyContext = createContext(
  defaultError<VoiceStudySnapshot>("VoiceStudyContext"),
);

/** Stable for the provider's lifetime: start/stop/setTurn never re-render the caller. */
export function useVoiceStudyController(): VoiceStudyController {
  const [controller] = useContextSelectors(VoiceStudyContext, (c) => c.controller);
  return controller;
}

/** Re-renders only on start / fail / stop — never per message or per card. */
export function useVoiceStudyLifecycle(): VoiceStudyLifecycle {
  const [lifecycle] = useContextSelectors(VoiceStudyContext, (c) => c.lifecycle);
  return lifecycle;
}

/** Inside Learn: whether a voice session is active. */
export function useVoiceStudyEnabled(): boolean {
  const [enabled] = useContextSelectors(VoiceStudyContext, (c) => c.lifecycle.kind === "active");
  return enabled;
}

/**
 * {@link useVoiceStudyEnabled} for code that also runs outside the Learn stack (the next-due query
 * key is built by navigators the web header and sign-in page call too): `false` there.
 */
export function useVoiceStudyEnabledIfProvided(): boolean {
  return use(VoiceStudyEnabledIfProvidedContext);
}

/** The active session's handle, or `null` when no session is active. */
export function useVoiceStudyActiveSession(): VoiceStudySessionHandle | null {
  const [session] = useContextSelectors(VoiceStudyContext, (c) =>
    c.lifecycle.kind === "active" ? c.lifecycle.session : null,
  );
  return session;
}

/** Whether a focused card has published a turn (or the voice-complete turn is showing). */
export function useVoiceStudyHasTurn(): boolean {
  const [hasTurn] = useContextSelectors(VoiceStudyContext, (c) => c.turn !== null);
  return hasTurn;
}

/** Only the focused screen can publish a turn; prefetched/covered cards stay silent. */
export function useVoiceStudyTurn(turn: VoiceStudyTurn | null) {
  const { setTurn } = useVoiceStudyController();
  const focused = useIsFocused();
  const handler = useLatest(turn?.onCommand);
  const id = turn?.id;
  const side = turn?.side;
  const glyph = turn?.glyph;
  const text = turn?.prompt.text;
  const clip = turn?.prompt.clip ?? null;
  const bargeIn = turn?.prompt.bargeIn;
  const hasHandler = turn?.onCommand !== undefined;
  const registered = useMemo<VoiceStudyTurn | null>(
    () =>
      id === undefined || side === undefined || text === undefined
        ? null
        : {
            glyph,
            id,
            onCommand: hasHandler
              ? (command) => handler.current?.(command) ?? Promise.resolve({ kind: "handled" })
              : undefined,
            prompt: { bargeIn, clip, text },
            side,
          },
    [id, side, glyph, text, clip, bargeIn, hasHandler, handler],
  );
  useEffect(() => {
    if (!focused || !registered) {
      return;
    }
    setTurn(registered);
    return () => setTurn((current) => (current === registered ? null : current));
  }, [focused, registered, setTurn]);
}
